import { Fragment, useState } from "react";
import { useMedia } from "react-use";
import useI18n from "../hooks/useI18n.js";
import {
  selectAppData,
  selectIsConnectedToRoom,
  selectPeers,
  selectPermissions,
  useHMSActions,
  useHMSStore,
  useRecordingStreaming,
} from "@100mslive/react-sdk";
import { RecordIcon, TranscriptIcon, WrenchIcon } from "@100mslive/react-icons";
import {
  Box,
  Button,
  config as cssConfig,
  Flex,
  Loading,
  Popover,
  Text,
  Tooltip,
} from "@100mslive/roomkit-react";
// import GoLiveButton from "../GoLiveButton";
// import { ResolutionInput } from "../Streaming/ResolutionInput";
// import { getResolution } from "../Streaming/RTMPStreaming";
// import { ToastManager } from "../Toast/ToastManager";
import { AdditionalRoomState, getRecordingText } from "./AdditionalRoomState";
import { useSidepaneToggle } from "../AppData/useSidepane";
// import { useSetAppDataByKey } from "../AppData/useUISettings";
import { getEntitlementsFromUrlJwt } from "../../services/entitlements";
import {
  APP_DATA,
  RTMP_RECORD_DEFAULT_RESOLUTION,
  SIDE_PANE_OPTIONS,
} from "../../common/constants";

export const LiveStatus = () => {
  const { t } = useI18n("common");
  const { isHLSRunning, isRTMPRunning } = useRecordingStreaming();
  if (!isHLSRunning && !isRTMPRunning) {
    return null;
  }
  const streamType = isHLSRunning ? "HLS" : "RTMP";
  return (
    <Flex align="center">
      <Box
        css={{
          w: "$4",
          h: "$4",
          r: "$round",
          bg: "$alert_error_default",
          mr: "$2",
        }}
      />
      <Text>
        {t("live")}
        <Text as="span" css={{ "@md": { display: "none" } }}>
          &nbsp;{t("with_stream", { stream: streamType })}
        </Text>
      </Text>
    </Flex>
  );
};

export const RecordingStatus = () => {
  const {
    isBrowserRecordingOn,
    isServerRecordingOn,
    isHLSRecordingOn,
    isRecordingOn,
  } = useRecordingStreaming();

  if (
    !isRecordingOn ||
    [!isHLSRecordingOn, isBrowserRecordingOn].every(value => !!value) ||
    [!isHLSRecordingOn, !isBrowserRecordingOn, isServerRecordingOn].every(value => !!value)
  ) {
    return null;
  }
  return (
    <Tooltip
      title={getRecordingText({
        isBrowserRecordingOn,
        isServerRecordingOn,
        isHLSRecordingOn,
      })}
    >
      <Box
        css={{
          color: "$alert_error_default",
        }}
      >
        <RecordIcon width={24} height={24} />
      </Box>
    </Tooltip>
  );
};

const TranscriptionStatus = () => {
  const { t } = useI18n("recording");
  const features = useHMSStore(selectAppData(APP_DATA.entitlements));
  const peers = useHMSStore(selectPeers);
  const hasRecruiter = peers.some(peer => ["host", "moderator"].includes(peer.roleName));
  if (features?.post_call_transcription !== true || !hasRecruiter) {
    return null;
  }
  return (
    <Tooltip title={t("transcription_notice")}>
      <Box
        css={{
          color: "$on_surface_medium",
          display: "flex",
          alignItems: "center",
        }}
      >
        <TranscriptIcon width={24} height={24} />
        <Text
          as="span"
          css={{ color: "currentColor", ml: "$2", "@md": { display: "none" } }}
        >
          {t("transcribing")}
        </Text>
      </Box>
    </Tooltip>
  );
};

const EndStream = () => {
  const { t } = useI18n('common');
  const toggleStreaming = useSidepaneToggle(SIDE_PANE_OPTIONS.STREAMING);

  return (
    <Button
      data-testid="end_stream"
      variant="danger"
      icon
      onClick={toggleStreaming}
    >
      <WrenchIcon />
      {t("manage_stream")}
    </Button>
  );
};

const StartRecording = () => {
  const { t } = useI18n('recording');
  const [resolution, setResolution] = useState(RTMP_RECORD_DEFAULT_RESOLUTION);
  const [open, setOpen] = useState(false);
  // const [recordingStarted, setRecordingState] = useSetAppDataByKey(
  //   APP_DATA.recordingStarted
  // );
  const { isBrowserRecordingOn, isStreamingOn, isHLSRunning } =
    useRecordingStreaming();
  const hmsActions = useHMSActions();
  const entitlements = getEntitlementsFromUrlJwt();
  if (isHLSRunning || entitlements?.recording === false) {
    return null;
  }
  if (isBrowserRecordingOn) {
    return (
      <Box css={{ color: "$alert_error_default", display: "flex", alignItems: "center" }}>
        <RecordIcon width={24} height={24} />
        <Text as="span" css={{ color: "currentColor", ml: "$2" }}>
          {t("recording")}
        </Text>
      </Box>
    );
  }
  // return (
  //   <Popover.Root open={open} onOpenChange={setOpen}>
  //     <Popover.Trigger asChild>
  //       <Button
  //         data-testid="start_recording"
  //         variant="standard"
  //         icon
  //         disabled={recordingStarted || isStreamingOn}
  //         onClick={() => setOpen(true)}
  //       >
  //         {recordingStarted ? (
  //           <Loading size={24} color="currentColor" />
  //         ) : (
  //           <RecordIcon />
  //         )}
  //         <Text
  //           as="span"
  //           css={{ "@md": { display: "none" }, color: "currentColor" }}
  //         >
  //           {recordingStarted
  //             ? t("starting_recording", { ns: "recording" })
  //             : t("start_recording", { ns: "recording" })}
  //         </Text>
  //       </Button>
  //     </Popover.Trigger>
  //     <Popover.Content align="end" sideOffset={8} css={{ w: "$64" }}>
  //       <ResolutionInput
  //         testId="recording_resolution"
  //         css={{ flexDirection: "column", alignItems: "start" }}
  //         onResolutionChange={setResolution}
  //       />
  //       <Button
  //         data-testid="start_recording_confirm"
  //         variant="primary"
  //         icon
  //         css={{ ml: "auto" }}
  //         type="submit"
  //         disabled={recordingStarted || isStreamingOn}
  //         onClick={async () => {
  //           try {
  //             setRecordingState(true);
  //             await hmsActions.startRTMPOrRecording({
  //               resolution: getResolution(resolution),
  //               record: true,
  //             });
  //           } catch (error) {
  //             if (error.message.includes("stream already running")) {
  //               ToastManager.addToast({
  //                 title: t("recording_already_running", { ns: "recording" }),
  //                 variant: "error",
  //               });
  //             } else {
  //               ToastManager.addToast({
  //                 title: error.message,
  //                 variant: "error",
  //               });
  //             }
  //             setRecordingState(false);
  //           }
  //           setOpen(false);
  //         }}
  //       >
  //         {t("start")}
  //       </Button>
  //     </Popover.Content>
  //   </Popover.Root>
  // );
};

export const StreamActions = () => {
  const isConnected = useHMSStore(selectIsConnectedToRoom);
  const permissions = useHMSStore(selectPermissions);
  const isMobile = useMedia(cssConfig.media.md);
  const { isStreamingOn } = useRecordingStreaming();

  return (
    <Flex align="center" css={{ gap: "$4" }}>
      <AdditionalRoomState />
      <Flex align="center" css={{ gap: "$4", "@md": { display: "none" } }}>
        <LiveStatus />
        <RecordingStatus />
      </Flex>
      {isConnected && !isMobile ? <StartRecording /> : null}
      {isConnected ? <TranscriptionStatus /> : null}
      {isConnected &&
        (permissions.hlsStreaming || permissions.rtmpStreaming) && (
          <Fragment>
            {isStreamingOn ? <EndStream /> : <GoLiveButton />}
          </Fragment>
        )}
    </Flex>
  );
};
